<template>
	<v-form
		ref="form"
		class="inline-form"
		lazy-validation
		@submit.prevent="submit"
	>
		<v-row
			class="prop-list-row"
			:class="{ editing, multiple: multipleValues }"
			:dense="editing"
			@click="rowClickHandler"
		>
			<v-col v-if="!editing" class="prop-key">{{ displayName }}</v-col>

			<v-col class="prop-value">
				<!-- Show interactive checkbox whether we're in inline edit mode or not -->
				<v-switch
					v-if="checkbox"
					v-model="fieldValue"
					class="align-end justify-end mt-0 pt-0"
					:node="node"
					:subpath="subpath"
					:disabled="!editable"
					hide-details
					dense
					@click.stop="rowClickHandler"
				/>
				<!-- Show input field if we're in inline edit mode -->
				<template v-else-if="editing">
					<v-row>
						<v-col>
							<k4-model-value-field
								v-model="fieldValue"
								:node="node"
								:subpath="subpath"
								inline
							/>
						</v-col>
					</v-row>

					<v-row v-if="submitError">
						<v-col class="pt-0">
							<p class="error-message">
								<b>Failed to change property value:</b>
								{{ submitError }}
							</p>
						</v-col>
					</v-row>

					<v-btn color="rust-red" small outlined @click.stop="finishEditing">
						<v-icon left>fas fa-times</v-icon>Cancel
					</v-btn>

					<v-btn
						class="ml-1"
						color="ocean darken-1"
						small
						outlined
						@click.stop="submit"
					>
						<v-icon left>far fa-save</v-icon>Save
					</v-btn>
				</template>
				<!-- Parse data if object -->
				<template v-else-if="multipleValues">
					<v-simple-table dense>
						<tr
							v-for="(value, key) of fieldValue"
							:key="key"
							class="light-mono"
						>
							<td>{{ propKey(key) }}</td>
							<td>{{ propValue(value) }}</td>
						</tr>
					</v-simple-table>
				</template>
				<!-- Otherwise just show the node value -->
				<k4-model-value v-else :node="node" :subpath="subpath" />
			</v-col>
		</v-row>
	</v-form>
</template>

<style lang="scss" scoped src="./PropList.scss"></style>

<style lang="scss" scoped>
.inline-form {
	width: 100%;
	margin: 0;
	padding: 0;
}

.error-message {
	text-align: left;
	white-space: pre-line;
	color: var(--v-error-base);
}
</style>

<script>
import $ from "jquery";
import _ from "lodash";
import { mapActions } from "vuex";

import base from "@/components/k4model/base";

export default {
	extends: base,

	props: {
		node: { type: Object, required: true },
		subpath: { type: String, required: true },
		editing: { type: Boolean, required: false },
	},

	data: () => ({
		value: undefined,
		submitError: false,
	}),

	computed: {
		editable() {
			return this.meta.editable || false;
		},

		checkbox() {
			return this.inputType === "checkbox";
		},

		multipleValues() {
			return !_.isString(this.fieldValue) && !!_.size(this.fieldValue);
		},

		fieldValue: {
			get() {
				return _.isUndefined(this.value) ? this.nodeValue : this.value;
			},
			set(val) {
				this.value = val;
			},
		},
	},

	watch: {
		editing(is, was) {
			if (!is && was) {
				// User clicked on another property, reset this form
				this.finishEditing({ ignoreErrors: true });
			} else {
				this.focusInput();
			}
		},

		value(is, was) {
			if (_.isUndefined(is)) return;

			// Whenever value changes, reset submit error.
			this.submitEror = false;

			// If checkbox value changes, automatically submit the form
			if (this.checkbox) this.submit();
		},
	},

	methods: {
		...mapActions({ setNodeProperty: "controller/nodes/setProperty" }),

		rowClickHandler($event) {
			// Stop click event propogation if there is no inline edit mode for
			// this node, or if we're already in edit mode.
			if (this.editing || !this.editable) {
				$event.stopPropagation();
				return;
			}

			if (this.checkbox) {
				// Toggle checkbox value when we click anywhere on the row
				this.fieldValue = !this.fieldValue;
			}
		},

		propKey(key) {
			const rawRgx = /(com\.)|(k4c)|[.-]/g
			return rawRgx.test(key) ? key : _.startCase(key);
		},

		propValue(value) {
			return value || "<>";
		},

		focusInput() {
			const $el = this.$el;
			this.$nextTick(() => {
				const input = $($el).find("input");
				if (input) input.focus();
			});
		},

		finishEditing() {
			if (this.editing) this.$emit("edit:done");
			this.submitError = false;
			this.value = undefined;
		},

		// When `ignoreErrors` is set we still fire the `edit:success` event,
		// even if there are validation errors. This resets the form without
		// submitting.
		async submit(opts = { ignoreErrors: false }) {
			// Form has errors
			if (!this.$refs.form.validate()) {
				if (opts.ignoreErrors) this.finishEditing();
				return;
			}

			// User never modified the field value
			if (this.value === undefined) {
				// End editing without changing anything
				this.finishEditing();
				return;
			}

			// Everything looks good
			try {
				await this.setNodeProperty({
					nodeGuid: this.node.meta.guid,
					propName: this.nodeName,
					propValue: this.value,
				});
				this.finishEditing();
			} catch (e) {
				this.submitError = e.message;
			}
		},
	},
};
</script>
